import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MaybeLayout } from '../components/layout/MaybeLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import api, { dailyBreakdownAPI, vacationsAPI, absencesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Download, RefreshCw, Activity, FileSpreadsheet, CalendarPlus } from 'lucide-react';
import { TooltipProvider } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { TimeHeatmap } from '../components/timetracking/TimeHeatmap';
import { StatusCards } from './timetracking-overview/StatusCards';
import { FiltersBar } from './timetracking-overview/FiltersBar';
import { EmployeeSummaryTable } from './timetracking-overview/EmployeeSummaryTable';
import { EmployeeSummaryCards } from './timetracking-overview/EmployeeSummaryCards';
import { OvertimeBalancesCard } from './timetracking-overview/OvertimeBalancesCard';
import { DetailedEntriesTable } from './timetracking-overview/DetailedEntriesTable';
import EmployeeTimeDrawer from './timetracking-overview/EmployeeTimeDrawer';
import ManualEntryDialog from './timetracking-overview/ManualEntryDialog';
import { DATEVExportDialog } from './timetracking-overview/DATEVExportDialog';
import {
  buildEmployeeSummary,
  computeStatusCounts,
  computeAbsenceCountsToday,
  getMonthStart,
  getMonthEnd,
  getWeekStart,
  getWeekEnd,
} from './timetracking-overview/utils';

export default function TimeTrackingOverview({ embedded = false }) {
  const { hasPermission } = useAuth();
  const canManageTimeTracking = hasPermission('time_tracking', 'view');
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [drawerEmp, setDrawerEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [dateRange, setDateRange] = useState({
    start: getWeekStart(),
    end: getWeekEnd(),
  });
  const [viewMode, setViewMode] = useState('week');
  const [displayMode, setDisplayMode] = useState('table');
  const [todayStatus, setTodayStatus] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [datevOpen, setDatevOpen] = useState(false);
  const [manualEntryOpen, setManualEntryOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [heatmapData, setHeatmapData] = useState(null);
  const [heatmapLoading, setHeatmapLoading] = useState(false);

  const loadHeatmap = useCallback(async () => {
    if (selectedEmployee === 'all') {
      setHeatmapData(null);
      return;
    }
    setHeatmapLoading(true);
    try {
      const { data } = await dailyBreakdownAPI.get({
        employee_id: selectedEmployee,
        start_date: dateRange.start,
        end_date: dateRange.end,
      });
      setHeatmapData(data);
    } catch (err) {
      console.error('heatmap load failed', err);
      setHeatmapData(null);
    } finally {
      setHeatmapLoading(false);
    }
  }, [selectedEmployee, dateRange]);

  useEffect(() => { loadHeatmap(); }, [loadHeatmap]);

  const loadData = useCallback(async () => {
    try {
      const startYear = new Date(dateRange.start).getFullYear();
      const params = {
        employee_id: selectedEmployee !== 'all' ? selectedEmployee : undefined,
        start_date: dateRange.start,
        end_date: dateRange.end,
        include_vacations: true,
      };
      if (selectedLocation !== 'all') params.location_id = selectedLocation;
      else if (selectedBranch !== 'all') params.branch_id = selectedBranch;

      const empParams = {};
      if (selectedLocation !== 'all') empParams.location_id = selectedLocation;
      else if (selectedBranch !== 'all') empParams.branch_id = selectedBranch;

      const [entriesRes, employeesRes, holidaysRes, branchesRes, locationsRes, vacationsRes, absencesRes] = await Promise.all([
        api.get('/time-tracking/entries', { params }),
        api.get('/employees', { params: empParams }),
        api.get(`/feiertage/${startYear}/flat`),
        api.get('/branches'),
        api.get('/locations'),
        vacationsAPI.getAll({ status: 'approved' }).catch(() => ({ data: [] })),
        absencesAPI.getAll().catch(() => ({ data: [] })),
      ]);

      setEntries(entriesRes.data);
      setEmployees(employeesRes.data);
      setHolidays(holidaysRes.data || []);
      setBranches(branchesRes.data || []);
      setLocations(locationsRes.data || []);
      setVacations(vacationsRes.data || []);
      setAbsences(absencesRes.data || []);

      const today = new Date().toISOString().split('T')[0];
      const todayEntries = entriesRes.data.filter(e => e.date === today);
      setTodayStatus(todayEntries);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, [selectedEmployee, dateRange, selectedBranch, selectedLocation]);

  useEffect(() => {
    loadData();
    if (!autoRefreshEnabled) return undefined;
    const interval = setInterval(() => { loadData(); }, 30000);
    return () => clearInterval(interval);
  }, [loadData, autoRefreshEnabled]);

  const changeViewMode = (mode) => {
    setViewMode(mode);
    if (mode === 'week') setDateRange({ start: getWeekStart(), end: getWeekEnd() });
    else setDateRange({ start: getMonthStart(), end: getMonthEnd() });
  };

  const navigatePeriod = (direction) => {
    const start = new Date(dateRange.start);
    const days = viewMode === 'week' ? 7 : 30;
    start.setDate(start.getDate() + (direction * days));
    const end = new Date(start);
    if (viewMode === 'week') end.setDate(end.getDate() + 6);
    else { end.setMonth(end.getMonth() + 1); end.setDate(0); }
    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
    });
  };

  const employeeSummary = useMemo(
    () => buildEmployeeSummary({ employees, entries, holidays, vacations, absences, dateRange, todayStatus }),
    [employees, entries, holidays, vacations, absences, dateRange, todayStatus],
  );

  const absenceCountsToday = useMemo(
    () => computeAbsenceCountsToday(employeeSummary),
    [employeeSummary],
  );

  const statusCounts = useMemo(
    () => computeStatusCounts(todayStatus, employees),
    [todayStatus, employees],
  );

  const handleExport = async () => {
    try {
      const params = {
        employee_id: selectedEmployee !== 'all' ? selectedEmployee : undefined,
        start_date: dateRange.start,
        end_date: dateRange.end,
      };
      if (selectedLocation !== 'all') params.location_id = selectedLocation;
      else if (selectedBranch !== 'all') params.branch_id = selectedBranch;
      const response = await api.get('/time-tracking/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `arbeitszeiten_${dateRange.start}_${dateRange.end}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export erfolgreich');
    } catch {
      toast.error('Export fehlgeschlagen');
    }
  };

  if (loading) {
    return (
      <MaybeLayout embedded={embedded}>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <Skeleton key={`skel-${i}`} className="h-24 rounded-xl" />)}
          </div>
          <Skeleton className="h-[500px] rounded-xl" />
        </div>
      </MaybeLayout>
    );
  }

  return (
    <MaybeLayout embedded={embedded}>
      <div className="space-y-6 animate-fade-in" data-testid="time-overview-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl text-foreground">Arbeitszeitübersicht</h1>
            <p className="text-muted-foreground mt-1">Alle Arbeitszeiten im Überblick</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="time-overview-header-actions">
            <button
              type="button"
              onClick={() => setAutoRefreshEnabled((v) => !v)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-colors border ${
                autoRefreshEnabled
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
                  : 'bg-muted/40 border-border text-muted-foreground'
              }`}
              title={autoRefreshEnabled ? 'Auto-Update aktiv (alle 30s) — klicken zum Pausieren' : 'Auto-Update pausiert — klicken zum Aktivieren'}
              data-testid="auto-refresh-toggle"
            >
              <span className="relative flex h-2 w-2">
                {autoRefreshEnabled && (
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                )}
                <span className={`relative inline-flex rounded-full h-2 w-2 ${autoRefreshEnabled ? 'bg-green-500' : 'bg-slate-400'}`} />
              </span>
              <span className="font-medium">{autoRefreshEnabled ? 'Live' : 'Pausiert'}</span>
              {lastUpdated && (
                <span className="text-[10px] opacity-75 hidden sm:inline" data-testid="last-updated">
                  · {lastUpdated.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
              )}
            </button>
            <Button variant="outline" onClick={loadData} size="sm" data-testid="manual-refresh-btn">
              <RefreshCw className="h-4 w-4 mr-1" />
              Aktualisieren
            </Button>
            {canManageTimeTracking && (
              <Button onClick={() => setManualEntryOpen(true)} size="sm" variant="secondary" data-testid="manual-entry-btn">
                <CalendarPlus className="h-4 w-4 mr-1" />
                Zeit nachtragen
              </Button>
            )}
            <Button onClick={handleExport} size="sm">
              <Download className="h-4 w-4 mr-1" />
              Excel Export
            </Button>
            <Button onClick={() => setDatevOpen(true)} size="sm" variant="default" data-testid="datev-export-btn">
              <FileSpreadsheet className="h-4 w-4 mr-1" />
              DATEV-Lohnübergabe
            </Button>
          </div>
        </div>

        <StatusCards statusCounts={statusCounts} absenceCounts={absenceCountsToday} />

        <FiltersBar
          canManageTimeTracking={canManageTimeTracking}
          branches={branches}
          locations={locations}
          employees={employees}
          selectedBranch={selectedBranch}
          setSelectedBranch={setSelectedBranch}
          selectedLocation={selectedLocation}
          setSelectedLocation={setSelectedLocation}
          selectedEmployee={selectedEmployee}
          setSelectedEmployee={setSelectedEmployee}
          viewMode={viewMode}
          changeViewMode={changeViewMode}
          displayMode={displayMode}
          setDisplayMode={setDisplayMode}
          navigatePeriod={navigatePeriod}
          dateRange={dateRange}
        />

        <Card className="border-border/60">
          <CardHeader>
            <CardTitle className="font-heading">Mitarbeiter-Übersicht</CardTitle>
            <CardDescription>Arbeitszeiten nach Mitarbeiter für den gewählten Zeitraum</CardDescription>
          </CardHeader>
          <CardContent>
            <TooltipProvider delayDuration={200}>
              {displayMode === 'table'
                ? <EmployeeSummaryTable employeeSummary={employeeSummary} onRowClick={setDrawerEmp} />
                : <EmployeeSummaryCards employeeSummary={employeeSummary} />}
            </TooltipProvider>
          </CardContent>
        </Card>

        <OvertimeBalancesCard
          branchId={selectedBranch}
          locationId={selectedLocation}
        />

        {selectedEmployee !== 'all' && (
          <Card className="border-border/60" data-testid="time-heatmap-card">
            <CardHeader className="flex flex-row items-start justify-between gap-2">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Activity className="h-5 w-5 text-primary" />
                  Tages-Heatmap
                </CardTitle>
                <CardDescription>
                  Klick auf einen Tag öffnet die Detail-Ansicht — grün = Plus-, rot = Minusstunden.
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              {heatmapLoading ? (
                <Skeleton className="h-32 w-full" />
              ) : (
                <TimeHeatmap
                  rows={heatmapData?.rows || []}
                  totals={heatmapData?.totals || null}
                  testIdPrefix="overview-heatmap"
                />
              )}
            </CardContent>
          </Card>
        )}

        <Card className="border-border/60">
          <CardHeader>
            <CardTitle className="font-heading">Detaillierte Einträge</CardTitle>
            <CardDescription>Alle Zeiteinträge im gewählten Zeitraum</CardDescription>
          </CardHeader>
          <CardContent>
            <DetailedEntriesTable entries={entries} />
          </CardContent>
        </Card>

        <EmployeeTimeDrawer
          open={!!drawerEmp}
          onOpenChange={(o) => { if (!o) setDrawerEmp(null); }}
          emp={drawerEmp}
          holidays={holidays}
          onCorrectionSubmitted={() => { loadData(); }}
        />

        <DATEVExportDialog
          open={datevOpen}
          onOpenChange={setDatevOpen}
          dateRange={dateRange}
          scope={{
            location_id: selectedLocation !== 'all' ? selectedLocation : undefined,
            branch_id: selectedBranch !== 'all' ? selectedBranch : undefined,
            employee_id: selectedEmployee !== 'all' ? selectedEmployee : undefined,
          }}
        />

        <ManualEntryDialog
          open={manualEntryOpen}
          onOpenChange={setManualEntryOpen}
          employees={employees}
          onSaved={() => { loadData(); }}
        />
      </div>
    </MaybeLayout>
  );
}
